import React, { useEffect, useRef, useState } from "react";
import { api } from "../../api";
import { ConditionsEditor } from "../Conditions";
import MessageEditor from "./MessageEditor";

/**
 * THE RESCUE — the evidence choice, the message, and what the last fortnight
 * would have done.
 *
 * THE EVIDENCE SETTING IS THE PAGE. Everything else here is a number in a box;
 * that one control decides whether this automation is safe, and it is built as a
 * choice between two readings with their consequences written out rather than as
 * a checkbox called "strict mode". Nothing in the record says "they did not turn
 * up" — see src/automations/noShowConfig.js — so the honest UI is one that shows
 * the two inferences and what each costs.
 *
 * AND THE PREVIEW PRICES IT. Of the calls in the last fortnight: how many are
 * confident no-shows, how many had somebody from their side on them, and how
 * many are silent either way. That third number is exactly what the looser
 * setting would buy and what it would risk, measured against his own calendar
 * rather than guessed at. It is the answer to the one question I could not
 * answer from the record — does Fireflies join every call — and it belongs on
 * screen next to the switch it decides.
 */

const EVIDENCE = [
  {
    key: "transcript",
    title: "Only when a transcript shows an empty room",
    doc:
      "Fireflies recorded the call and every participant was one of ours: you joined, waited, and nobody came. Positive proof the meeting ran and that they were not in it.",
    risk: "A call Fireflies never joined is never rescued.",
  },
  {
    key: "transcript_or_silence",
    title: "Also when no transcript arrived at all",
    doc:
      "Treats silence as a no-show. Catches the calls Fireflies missed, and the ones nobody dialled into.",
    risk:
      "Silence also means a phone call, an uninvited bot, or a bot that failed. Each of those apologises to somebody who was there.",
  },
];

/* ------------------------------------------------------------------ *
 * The preview.
 * ------------------------------------------------------------------ */

function Preview({ id, config }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const latest = useRef(0);

  useEffect(() => {
    const token = ++latest.current;
    const timer = setTimeout(() => {
      setLoading(true);
      api(`/automations/${id}/no-shows/preview`, {
        method: "POST",
        body: JSON.stringify({ config, days: 14 }),
      })
        .then((next) => {
          if (token === latest.current) setResult(next);
        })
        .catch((err) => {
          if (token === latest.current) setResult({ errors: [err.message], calls: [] });
        })
        .finally(() => {
          if (token === latest.current) setLoading(false);
        });
    }, 500);
    return () => clearTimeout(timer);
  }, [id, config]);

  const calls = (result && result.calls) || [];
  const errors = (result && result.errors) || [];

  return (
    <aside className="crm-card crm-auto-preview">
      <header className="crm-auto-preview__head">
        <h3>The last fortnight</h3>
        {loading && <span className="crm-hint">checking…</span>}
      </header>

      {!!errors.length && <div className="crm-rb__err">{errors.join(" ")}</div>}

      {!errors.length && result && (
        <>
          <p className="crm-auto-count">
            <b>{result.would_rescue}</b> of {result.considered} would have been rescued.
          </p>
          {result.only_on_silence > 0 && (
            <p className="crm-hint crm-hint--warn">
              {result.only_on_silence} more {result.only_on_silence === 1 ? "call is" : "calls are"} silent
              either way — no transcript arrived, so nothing says whether they came.
              {config.evidence === "transcript"
                ? " The looser setting would rescue those."
                : " The setting you have picked rescues those."}
            </p>
          )}
        </>
      )}

      {!calls.length && !loading && !errors.length && (
        <p className="crm-hint">No calls behind us in the last fortnight.</p>
      )}

      <ul className="crm-auto-preview__list">
        {calls.map((call) => (
          <li key={call.event_id} className={call.would_rescue ? "" : "is-skipped"}>
            <div className="crm-auto-preview__who">
              <b>{call.company || call.contact}</b>
              <span className="crm-hint">
                {new Date(call.call_at).toLocaleString(undefined, {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
            <p className={`crm-hint${call.would_rescue ? "" : " crm-hint--warn"}`}>
              {call.would_rescue
                ? "Nobody from their side was on it"
                : call.skipped_because}
            </p>
          </li>
        ))}
      </ul>
    </aside>
  );
}

/* ------------------------------------------------------------------ *
 * The rendered email.
 * ------------------------------------------------------------------ */

function Rendered({ id, config }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const latest = useRef(0);

  useEffect(() => {
    const token = ++latest.current;
    const timer = setTimeout(() => {
      setLoading(true);
      api(`/automations/${id}/no-shows/render`, {
        method: "POST",
        body: JSON.stringify({ config }),
      })
        .then((next) => {
          if (token === latest.current) setPreview(next);
        })
        .catch((err) => {
          if (token === latest.current) setPreview({ errors: [err.message] });
        })
        .finally(() => {
          if (token === latest.current) setLoading(false);
        });
    }, 500);
    return () => clearTimeout(timer);
  }, [id, config]);

  if (!preview) return <p className="crm-hint">{loading ? "Rendering…" : ""}</p>;

  if (preview.errors?.length) return <div className="crm-rb__err">{preview.errors.join(" ")}</div>;
  if (preview.error) return <p className="crm-hint crm-hint--warn">{preview.error}</p>;

  if (!preview.ok) {
    return (
      <div className="crm-rb__err">
        {preview.calendar_error
          ? `The calendar would not answer, so no times could be offered: ${preview.calendar_error}`
          : preview.no_times
          ? "Your calendar has no free times inside the bookable window, and this email promises some. It would be held back rather than sent with an empty list. Widen the window on the Calendar tab, or write the body around {{availability}}, which says the position in words."
          : `This would not send. The template needs ${(preview.missing || []).join(", ")}, and this call has none.`}
      </div>
    );
  }

  return (
    <div className="crm-auto-rendered">
      <p className="crm-hint">
        Against your call with <b>{preview.against.contact}</b>
        {preview.against.company ? ` at ${preview.against.company}` : ""}, times in{" "}
        {preview.against.timezone}
        {preview.against.timezone_source === "fallback" ? " (we do not know their country)" : ""}.
      </p>
      <div className="crm-auto-rendered__mail">
        <div className="crm-auto-rendered__subject">{preview.subject}</div>
        <div dangerouslySetInnerHTML={{ __html: preview.body }} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The tab.
 * ------------------------------------------------------------------ */

export default function NoShowTab({ id, config, patch, meta }) {
  const fields = meta?.filter_vocabulary || [];
  const variables = meta?.variables || [];

  return (
    <div className="crm-auto-two">
      <div className="crm-auto-two__main">
        <section className="crm-card">
          <h3>What counts as nobody turning up</h3>
          <p className="crm-hint">
            Nothing on the record says they did not come. These are the two ways to infer it, and
            they are not equally safe.
          </p>
          <div className="crm-auto-evidence">
            {EVIDENCE.map((option) => (
              <label
                key={option.key}
                className={`crm-auto-evidence__opt${config.evidence === option.key ? " is-on" : ""}`}
              >
                <input
                  type="radio"
                  name="evidence"
                  checked={config.evidence === option.key}
                  onChange={() => patch("evidence", option.key)}
                />
                <span>
                  <b>{option.title}</b>
                  <span className="crm-hint">{option.doc}</span>
                  <span className="crm-hint crm-hint--warn">{option.risk}</span>
                </span>
              </label>
            ))}
          </div>
        </section>

        <section className="crm-card">
          <h3>Which calls</h3>
          <ConditionsEditor
            conditions={config.entry?.conditions}
            fields={fields}
            title="Rescue a call when the company"
            emptyNote="No filter, so every missed call with a known contact is rescued."
            onChange={(next) => patch("entry", { conditions: next })}
          />
        </section>

        <section className="crm-card crm-auto-timing">
          <h3>Timing</h3>
          <label className="crm-auto-field">
            <span className="crm-label">Wait after the call ends</span>
            <span className="crm-auto-field__row">
              <input
                className="crm-input"
                type="number"
                min="5"
                max="1440"
                value={config.timing?.grace_minutes ?? 90}
                onChange={(event) =>
                  patch("timing", { grace_minutes: Number(event.target.value) || 90 })
                }
              />
              <span>minutes</span>
            </span>
            <span className="crm-hint">
              It has to outlast Fireflies uploading the transcript and our own five-minute poll
              picking it up, or a real transcript arrives after the decision was made.
            </span>
          </label>

          <label className="crm-auto-field">
            <span className="crm-label">Only look back</span>
            <span className="crm-auto-field__row">
              <input
                className="crm-input"
                type="number"
                min="1"
                max="336"
                value={config.timing?.lookback_hours ?? 48}
                onChange={(event) =>
                  patch("timing", { lookback_hours: Number(event.target.value) || 48 })
                }
              />
              <span>hours</span>
            </span>
            <span className="crm-hint">
              So a deploy after a quiet weekend does not apologise for a call from Thursday.
            </span>
          </label>
        </section>

        <section className="crm-card">
          <h3>The email</h3>
          <MessageEditor
            subject={config.message?.subject || ""}
            body={config.message?.body || ""}
            variables={variables}
            rows={14}
            label="Rescue"
            subjectHint={
              config.template?.thread === "continue"
                ? "Used when there is no thread to reply into. Otherwise the reply takes that thread's subject, prefixed “Re:”."
                : "The rescue starts a new thread with this subject."
            }
            onChange={(next) => patch("message", next)}
          />
          <p className="crm-hint">
            It has to offer a way to rebook: {"{{#slots}}"} expands into your real free times,
            {" "}
            {"{{availability}}"} says them in words when their timezone is a guess, and
            {" "}
            {"{{booking_link}}"} uses the link from the Calendar tab. A body with none of those is
            refused.
          </p>
        </section>

        <section className="crm-card">
          <h3>What it would send</h3>
          <Rendered id={id} config={config} />
        </section>
      </div>

      <Preview id={id} config={config} />
    </div>
  );
}
